import {
  getPendingSaasHandoffBootstrap,
  getPendingSaasHandoffIntent,
} from './saasHandoffIntent';

const SAAS_HANDOFF_QUERY_KEYS = [
  'run_source',
  'run_entry_path',
  'run_store_url',
  'run_session_id',
  'ownerAccessToken',
  'return_to',
  'run_target_host',
  'run_target_tenant',
] as const;

export type SaasHandoffBootstrapPayload = {
  username: string;
  sessionId?: string;
};

export type SaasHandoffResumeRequest = {
  tenantId: string;
  ownerUserId: number;
  ownerAccessToken: string;
  returnTo?: string;
  targetHost?: string;
  targetTenantId?: string;
};

type OwnerAccessTokenClaims = {
  tenantId?: unknown;
  ownerUserId?: unknown;
};

function decodeOwnerAccessTokenClaims(token?: string): OwnerAccessTokenClaims | undefined {
  const normalized = token?.trim();
  if (!normalized) {
    return undefined;
  }

  const payload = normalized.split('.')[0];
  if (!payload) {
    return undefined;
  }

  try {
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded && typeof decoded === 'object' ? decoded as OwnerAccessTokenClaims : undefined;
  } catch {
    return undefined;
  }
}

export function buildSaasHandoffBootstrap(params: {
  currentUserId?: string;
  search?: string;
  getIntent?: typeof getPendingSaasHandoffIntent;
  getBootstrapHint?: typeof getPendingSaasHandoffBootstrap;
}): SaasHandoffBootstrapPayload | undefined {
  const {
    currentUserId,
    search = window.location.search,
    getIntent = getPendingSaasHandoffIntent,
    getBootstrapHint = getPendingSaasHandoffBootstrap,
  } = params;

  if (!currentUserId) {
    return undefined;
  }

  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const intent = getIntent();
  if (intent && intent.source !== 'saas') {
    return undefined;
  }

  const bootstrapHint = getBootstrapHint();
  if (bootstrapHint && bootstrapHint.source !== 'saas') {
    return undefined;
  }

  const querySource = searchParams.get('run_source');
  const hasExplicitNonSaasSearch = searchParams.toString().length > 0 && !querySource;
  if (hasExplicitNonSaasSearch) {
    return undefined;
  }

  const resolvedSource = intent?.source || querySource || bootstrapHint?.source;
  if (resolvedSource !== 'saas') {
    return undefined;
  }

  const sessionId = intent?.sessionId || bootstrapHint?.sessionId || searchParams.get('run_session_id') || undefined;
  return {
    username: `run-sage-${currentUserId}`,
    sessionId,
  };
}

export function buildLegacySaasHandoffText(params: {
  search?: string;
  getIntent?: typeof getPendingSaasHandoffIntent;
  getBootstrapHint?: typeof getPendingSaasHandoffBootstrap;
}) {
  const {
    search = window.location.search,
    getIntent = getPendingSaasHandoffIntent,
    getBootstrapHint = getPendingSaasHandoffBootstrap,
  } = params;

  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const intent = getIntent();
  if (intent && intent.source !== 'saas') {
    return undefined;
  }

  const bootstrapHint = getBootstrapHint();
  if (bootstrapHint && bootstrapHint.source !== 'saas') {
    return undefined;
  }

  const querySource = searchParams.get('run_source');
  const hasExplicitNonSaasSearch = searchParams.toString().length > 0 && !querySource;
  if (hasExplicitNonSaasSearch) {
    return undefined;
  }

  const resolvedSource = intent?.source || querySource || bootstrapHint?.source;
  if (resolvedSource !== 'saas') {
    return undefined;
  }

  const entryPath =
    intent?.entryPath || bootstrapHint?.entryPath || searchParams.get('run_entry_path') || 'customer-service';
  const storeUrl = normalizeStoreUrl(
    intent?.storeUrl
    || bootstrapHint?.storeUrl
    || searchParams.get('run_store_url')
    || intent?.lastUserMessage,
  );
  const sessionId = intent?.sessionId || bootstrapHint?.sessionId || searchParams.get('run_session_id') || undefined;

  const sessionSuffix = sessionId ? ` SaaS handoff session is ${sessionId}.` : '';
  if (storeUrl) {
    return [
      `I just came from Run setup for ${entryPath}.`,
      `My store URL is ${storeUrl}.${sessionSuffix}`,
      'Continue onboarding from here.',
    ].join(' ');
  }
  return [
    `I just came from Run setup for ${entryPath}.${sessionSuffix}`,
    'Continue onboarding from here.',
  ].join(' ');
}

export function buildSaasHandoffResumeRequest(params: {
  currentUserId?: string;
  search?: string;
  getIntent?: typeof getPendingSaasHandoffIntent;
  getBootstrapHint?: typeof getPendingSaasHandoffBootstrap;
}): SaasHandoffResumeRequest | undefined {
  const {
    currentUserId,
    search = window.location.search,
    getIntent = getPendingSaasHandoffIntent,
    getBootstrapHint = getPendingSaasHandoffBootstrap,
  } = params;

  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const intent = getIntent();
  const bootstrapHint = getBootstrapHint();
  const ownerAccessToken =
    intent?.ownerAccessToken
    || bootstrapHint?.ownerAccessToken
    || searchParams.get('ownerAccessToken')
    || undefined;

  if (!ownerAccessToken) {
    return undefined;
  }

  const tokenClaims = decodeOwnerAccessTokenClaims(ownerAccessToken);
  const tokenOwnerUserId =
    typeof tokenClaims?.ownerUserId === 'number' && Number.isFinite(tokenClaims.ownerUserId)
      ? tokenClaims.ownerUserId
      : typeof tokenClaims?.ownerUserId === 'string' && /^\d+$/.test(tokenClaims.ownerUserId)
        ? Number(tokenClaims.ownerUserId)
        : 0;
  const tokenTenantId =
    typeof tokenClaims?.tenantId === 'string' && tokenClaims.tenantId.trim()
      ? tokenClaims.tenantId.trim()
      : '';
  const resolvedOwnerUserId =
    tokenOwnerUserId
    || (currentUserId && /^\d+$/.test(currentUserId) ? Number(currentUserId) : 0);
  const resolvedTenantId =
    tokenTenantId
    || (resolvedOwnerUserId && currentUserId && /^\d+$/.test(currentUserId) ? `run-${currentUserId}` : '');

  if (!resolvedOwnerUserId || !resolvedTenantId) {
    return undefined;
  }

  const returnTo = bootstrapHint?.returnTo || searchParams.get('return_to') || undefined;
  const targetHost = bootstrapHint?.targetHost || searchParams.get('run_target_host') || undefined;
  const targetTenantId = bootstrapHint?.targetTenantId || searchParams.get('run_target_tenant') || undefined;

  return {
    tenantId: resolvedTenantId,
    ownerUserId: resolvedOwnerUserId,
    ownerAccessToken,
    ...(returnTo ? { returnTo } : {}),
    ...(targetHost ? { targetHost } : {}),
    ...(targetTenantId ? { targetTenantId } : {}),
  };
}

export function clearSaasHandoffSearchParams(url: string): string {
  const currentUrl = new URL(url, 'https://run.ceo');
  SAAS_HANDOFF_QUERY_KEYS.forEach((key) => currentUrl.searchParams.delete(key));
  return `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
}

function normalizeStoreUrl(value?: string | null) {
  const normalized = value?.trim();
  return normalized || undefined;
}
